/**
 * Dahua enterprise NVR catalog — WizSense 5000-EI, WizMind 5-XI / 5-XI/Pro,
 * WizMind 600-XI (NVR608*-XI), WizMind 616-XI 3U flagship, and EVS Embedded
 * Video Storage 5/8-series.
 *
 * Every SKU below is verified against a primary Dahua datasheet PDF fetched on
 * 2026-05-14 from material.dahuasecurity.com / materialfile.dahuasecurity.com.
 * Source URL appears as a comment above each entry. Fields are left undefined
 * when the datasheet doesn't list a value — never guessed.
 *
 * NVR700 series: the current (2026) Dahua catalog does NOT list any NVR7xx
 * model. The 8-HDD enterprise tier sits in the 600-XI family (NVR608*-XI) and
 * the 16-HDD flagship in 616-XI (NVR616DH/DRH). Higher channel-count needs are
 * served by the EVS5/EVS8 series.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const DAHUA_NVR_5000_6000: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // WizSense 5000-EI Series — AcuPick, 2-HDD compact 1U
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5208-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5208-ei',
    manufacturer: 'Dahua',
    model: 'NVR5208-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense 1U NVR — 2 SATA (20 TB each), 32 MP cameras, AcuPick 8-ch',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5208-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5208-8P-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5208-8p-ei',
    manufacturer: 'Dahua',
    model: 'NVR5208-8P-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense 1U NVR — 8-port PoE/ePoE (130 W), 2 SATA (20 TB), AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5208-8P-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 8,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5216-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5216-ei',
    manufacturer: 'Dahua',
    model: 'NVR5216-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1U NVR — 2 SATA (20 TB each), 32 MP support, AcuPick 16-ch',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5216-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5216-16P-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5216-16p-ei',
    manufacturer: 'Dahua',
    model: 'NVR5216-16P-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1U NVR — 16-port PoE/ePoE (130 W), 2 SATA (20 TB), AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5216-16P-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 16,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5232-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5232-ei',
    manufacturer: 'Dahua',
    model: 'NVR5232-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1U NVR — 2 SATA (20 TB), 32 MP support, AcuPick 32-ch',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5232-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizSense 5000-EI Series — 4-HDD 1.5U mid-range
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5432-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5432-ei',
    manufacturer: 'Dahua',
    model: 'NVR5432-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1.5U NVR — 4 SATA (20 TB each), dual GbE, AcuPick 32-ch',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5432-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      // Dahua labels chassis as 1.5U; closest catalog enum is 2U.
      formFactor: '2U',
      alarmIO: '16 in / 6 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5432-16P-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5432-16p-ei',
    manufacturer: 'Dahua',
    model: 'NVR5432-16P-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1.5U NVR — 16-port PoE/ePoE (150 W), 4 SATA (20 TB), AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5432-16P-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 20,
      poeInPorts: 16,
      poeBudgetW: 150,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      // Dahua labels chassis as 1.5U; closest catalog enum is 2U.
      formFactor: '2U',
      alarmIO: '16 in / 6 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizSense 5000-EI Series — 8-HDD 2U high-channel-count
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5816-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5816-ei',
    manufacturer: 'Dahua',
    model: 'NVR5816-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 2U NVR — 8 SATA (20 TB each), dual GbE, AcuPick 16-ch',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5816-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5832-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5832-ei',
    manufacturer: 'Dahua',
    model: 'NVR5832-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 2U NVR — 8 SATA (20 TB), dual GbE, 32 MP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5832-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5864-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5864-ei',
    manufacturer: 'Dahua',
    model: 'NVR5864-EI',
    category: 'nvr-recorder',
    tagline: '64-ch WizSense 2U NVR — 8 SATA (20 TB), dual GbE, 32 MP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5864-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizMind NVR5-XI Series — 8-HDD enterprise 2U with RAID
  // ─────────────────────────────────────────────────────────────────────

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5816H-XI_S0_datasheet_20240509.pdf
  {
    id: 'dahua-nvr5816h-xi',
    manufacturer: 'Dahua',
    model: 'NVR5816H-XI',
    category: 'nvr-recorder',
    tagline: '16-ch WizMind 2U NVR — 8 SATA (20 TB), RAID 0/1/5/6/10, hot-swap, AcuPick 16-ch',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5816H-XI_S0_datasheet_20240509.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 512,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5816RH-XI_S0_datasheet_20240509.pdf
  {
    id: 'dahua-nvr5816rh-xi',
    manufacturer: 'Dahua',
    model: 'NVR5816RH-XI',
    category: 'nvr-recorder',
    tagline: '16-ch WizMind 2U NVR — redundant PSU, 8 SATA (20 TB), RAID 0/1/5/6/10, hot-swap',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5816RH-XI_S0_datasheet_20240509.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 512,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5832H-XI_S0_datasheet_20240509.pdf
  {
    id: 'dahua-nvr5832h-xi',
    manufacturer: 'Dahua',
    model: 'NVR5832H-XI',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 2U NVR — 8 SATA (20 TB), RAID 0/1/5/6/10, hot-swap, dual GbE',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5832H-XI_S0_datasheet_20240509.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 512,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5832RH-XI_S0_datasheet_20240509.pdf
  {
    id: 'dahua-nvr5832rh-xi',
    manufacturer: 'Dahua',
    model: 'NVR5832RH-XI',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 2U NVR — redundant PSU, 8 SATA (20 TB), RAID 0/1/5/6/10, hot-swap',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5832RH-XI_S0_datasheet_20240509.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 512,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizMind NVR5-XI/Pro Series (WizSeek AI search, refreshed 2025)
  // ─────────────────────────────────────────────────────────────────────

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-NVR5432-XIPro_S0_datasheet_20250916.pdf
  {
    id: 'dahua-nvr5432-xi-pro',
    manufacturer: 'Dahua',
    model: 'NVR5432-XI/Pro',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 1.5U NVR — 4 SATA (20 TB), dual GbE, WizSeek AI search',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-NVR5432-XIPro_S0_datasheet_20250916.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      // Dahua labels chassis as 1.5U; closest catalog enum is 2U.
      formFactor: '2U',
      alarmIO: '16 in / 6 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-NVR5432-16HP-XIPro_S0_datasheet_20250916.pdf
  {
    id: 'dahua-nvr5432-16hp-xi-pro',
    manufacturer: 'Dahua',
    model: 'NVR5432-16HP-XI/Pro',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 1.5U NVR — 16-port High-PoE/ePoE (200 W), 4 SATA, WizSeek',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-NVR5432-16HP-XIPro_S0_datasheet_20250916.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 20,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      // Dahua labels chassis as 1.5U; closest catalog enum is 2U.
      formFactor: '2U',
      alarmIO: '16 in / 6 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-NVR5832-16P-XIPro_S0_datasheet_20250916.pdf
  {
    id: 'dahua-nvr5832-16p-xi-pro',
    manufacturer: 'Dahua',
    model: 'NVR5832-16P-XI/Pro',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 2U NVR — 16-port PoE/ePoE (150 W), 8 SATA (20 TB), WizSeek AI',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-NVR5832-16P-XIPro_S0_datasheet_20250916.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 16,
      poeBudgetW: 150,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-NVR5864-XIPro_S0_datasheet_20250916.pdf
  {
    id: 'dahua-nvr5864-xi-pro',
    manufacturer: 'Dahua',
    model: 'NVR5864-XI/Pro',
    category: 'nvr-recorder',
    tagline: '64-ch WizMind 2U NVR — 8 SATA (20 TB), dual GbE, WizSeek AI search',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-NVR5864-XIPro_S0_datasheet_20250916.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizMind 600-XI Series (NVR608*-XI) — 8-HDD 2U with 2.5 GbE & RAID
  // ─────────────────────────────────────────────────────────────────────

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-32-XI_S0_datasheet_20240511.pdf
  {
    id: 'dahua-nvr608h-32-xi',
    manufacturer: 'Dahua',
    model: 'NVR608H-32-XI',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 2U NVR — 8 SATA (20 TB), RAID 0/1/5/6/10, hot-swap, AcuPick 32-ch',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-32-XI_S0_datasheet_20240511.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-64-XI_S0_datasheet_20240511.pdf
  {
    id: 'dahua-nvr608h-64-xi',
    manufacturer: 'Dahua',
    model: 'NVR608H-64-XI',
    category: 'nvr-recorder',
    tagline: '64-ch WizMind 2U NVR — 8 SATA (20 TB), RAID 0/1/5/6/10, hot-swap, 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-64-XI_S0_datasheet_20240511.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-128-XI_S0_datasheet_20240511.pdf
  {
    id: 'dahua-nvr608h-128-xi',
    manufacturer: 'Dahua',
    model: 'NVR608H-128-XI',
    category: 'nvr-recorder',
    tagline: '128-ch WizMind 2U NVR — 8 SATA (20 TB), RAID 0/1/5/6/10, AcuPick 64-ch',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-128-XI_S0_datasheet_20240511.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-32-XI_S0_datasheet_20240511.pdf
  {
    id: 'dahua-nvr608rh-32-xi',
    manufacturer: 'Dahua',
    model: 'NVR608RH-32-XI',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 2U NVR — redundant PSU, 8 SATA (20 TB), RAID 0/1/5/6/10, hot-swap',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-32-XI_S0_datasheet_20240511.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-64-XI_S0_datasheet_20240511.pdf
  {
    id: 'dahua-nvr608rh-64-xi',
    manufacturer: 'Dahua',
    model: 'NVR608RH-64-XI',
    category: 'nvr-recorder',
    tagline: '64-ch WizMind 2U NVR — redundant PSU, 8 SATA (20 TB), RAID 0/1/5/6/10, 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-64-XI_S0_datasheet_20240511.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-128-XI_S0_datasheet_20240511.pdf
  {
    id: 'dahua-nvr608rh-128-xi',
    manufacturer: 'Dahua',
    model: 'NVR608RH-128-XI',
    category: 'nvr-recorder',
    tagline: '128-ch WizMind 2U NVR — redundant PSU, 8 SATA, RAID 0/1/5/6/10, AcuPick 64-ch',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-128-XI_S0_datasheet_20240511.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizMind 616-XI Series (NVR616D*-XI) — 16-HDD 3U flagship
  // ─────────────────────────────────────────────────────────────────────

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR616DH-64-XI_S0_datasheet_20240511.pdf
  {
    id: 'dahua-nvr616dh-64-xi',
    manufacturer: 'Dahua',
    model: 'NVR616DH-64-XI',
    category: 'nvr-recorder',
    tagline: '64-ch WizMind 3U NVR — 16 SATA (20 TB), RAID 0/1/5/6/10, LCD, AcuPick 64-ch',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR616DH-64-XI_S0_datasheet_20240511.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 16,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      alarmIO: '16 in / 8 out',
      networkPorts: '4× 10/100/1000/2500 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR616DH-128-XI_S0_datasheet_20231021.pdf
  {
    id: 'dahua-nvr616dh-128-xi',
    manufacturer: 'Dahua',
    model: 'NVR616DH-128-XI',
    category: 'nvr-recorder',
    tagline: '128-ch WizMind 3U NVR — 16 SATA (20 TB), RAID 0/1/5/6/10, LCD, 4× 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR616DH-128-XI_S0_datasheet_20231021.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 16,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      alarmIO: '16 in / 8 out',
      networkPorts: '4× 10/100/1000/2500 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR616DRH-64-XI_S0_datasheet_20231021.pdf
  {
    id: 'dahua-nvr616drh-64-xi',
    manufacturer: 'Dahua',
    model: 'NVR616DRH-64-XI',
    category: 'nvr-recorder',
    tagline: '64-ch WizMind 3U NVR — redundant PSU, 16 SATA, RAID 0/1/5/6/10, LCD, 4× 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR616DRH-64-XI_S0_datasheet_20231021.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 16,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      alarmIO: '16 in / 8 out',
      networkPorts: '4× 10/100/1000/2500 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR616DRH-128-XI_S0_datasheet_20240511.pdf
  {
    id: 'dahua-nvr616drh-128-xi',
    manufacturer: 'Dahua',
    model: 'NVR616DRH-128-XI',
    category: 'nvr-recorder',
    tagline: '128-ch WizMind 3U NVR — redundant PSU, 16 SATA, RAID 0/1/5/6/10, LCD, 4× 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR616DRH-128-XI_S0_datasheet_20240511.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 16,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      alarmIO: '16 in / 8 out',
      networkPorts: '4× 10/100/1000/2500 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // EVS Embedded Video Storage — rack-mount enterprise tier
  // ─────────────────────────────────────────────────────────────────────

  // https://materialfile.dahuasecurity.com/uploads/cpq/84130/datasheet/EVS5124S_V3_datasheet_20250704.pdf
  {
    id: 'dahua-evs5124s',
    manufacturer: 'Dahua',
    model: 'EVS5124S',
    category: 'nvr-recorder',
    tagline: '320-ch 4U EVS — 24-bay SATA hot-swap, RAID 0/1/5/6/10/50/60, redundant PSU',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/84130/datasheet/EVS5124S_V3_datasheet_20250704.pdf',
    specs: {
      channels: 320,
      inputBandwidthMbps: 800,
      outputBandwidthMbps: 800,
      maxResolution: '32 MP',
      hddBays: 24,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10', 'JBOD'],
      onvif: true,
      formFactor: '4U',
      networkPorts: '4× 1 GbE RJ-45 (+ optional 2× 10 GbE SFP+)',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/86800/datasheet/DHI-EVS5136S_V3_datasheet_20240516.pdf
  {
    id: 'dahua-evs5136s',
    manufacturer: 'Dahua',
    model: 'EVS5136S',
    category: 'nvr-recorder',
    tagline: '320-ch 4U EVS — 36-bay SATA hot-swap, RAID 0/1/5/6/10/50/60, redundant PSU',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/86800/datasheet/DHI-EVS5136S_V3_datasheet_20240516.pdf',
    specs: {
      channels: 320,
      inputBandwidthMbps: 800,
      outputBandwidthMbps: 800,
      maxResolution: '32 MP',
      hddBays: 36,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10', 'JBOD'],
      onvif: true,
      formFactor: '4U',
      networkPorts: '4× 1 GbE RJ-45 (+ optional 2× 10 GbE SFP+)',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/86799/datasheet/EVS5148S_V3_datasheet_20240516.pdf
  {
    id: 'dahua-evs5148s',
    manufacturer: 'Dahua',
    model: 'EVS5148S',
    category: 'nvr-recorder',
    tagline: '320-ch 6U EVS — 48-bay SATA hot-swap, RAID 0/1/5/6/10/50/60, redundant PSU',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/86799/datasheet/EVS5148S_V3_datasheet_20240516.pdf',
    specs: {
      channels: 320,
      inputBandwidthMbps: 800,
      outputBandwidthMbps: 800,
      maxResolution: '32 MP',
      hddBays: 48,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10', 'JBOD'],
      onvif: true,
      // Catalog form-factor enum tops out at 4U; physical chassis is 6U.
      formFactor: '4U',
      networkPorts: '4× 1 GbE RJ-45 (+ optional 2× 10 GbE SFP+)',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-EVS8248X_datasheet_20251011.pdf
  {
    id: 'dahua-evs8248x',
    manufacturer: 'Dahua',
    model: 'EVS8248X',
    category: 'nvr-recorder',
    tagline: '1024-ch 6U EVS flagship — 48-bay SAS/SATA, RAID 0/1/5/6/10/50/60, 2+1 PSU',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-EVS8248X_datasheet_20251011.pdf',
    specs: {
      channels: 1024,
      inputBandwidthMbps: 2048,
      outputBandwidthMbps: 2048,
      maxResolution: '32 MP',
      hddBays: 48,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10', 'JBOD'],
      onvif: true,
      // Catalog form-factor enum tops out at 4U; physical chassis is 6U.
      formFactor: '4U',
      networkPorts: '1× 1 GbE mgmt + 4× 1 GbE LAN (+ optional 4× 10 GbE SFP+)',
    },
  },
];
